import React, { useContext } from "react";
import { AutoAwesomeRounded, BusinessRounded } from "@mui/icons-material";
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AppVarsContext } from "../../context/AppVarsContext";
import GlassPageShell from "../../components/glass/GlassPageShell";
import GlassPanel from "../../components/glass/GlassPanel";
import EcommerceStatus from "../ecommerce-status";

const Hompepage = ({ clientID }) => {
  const { appVars, appLoading, appAppearance } = useContext(AppVarsContext);

  const softChipSx = {
    height: 34,
    borderRadius: "999px",
    bgcolor: alpha("#ffffff", 0.58),
    border: `1px solid ${appAppearance.colors.border}`,
    "& .MuiChip-label": {
      px: 1.4,
      fontWeight: 700,
      color: appAppearance.colors.textPrimary,
    },
  };

  const heroAside = (
    <GlassPanel
      sx={{
        height: "100%",
        p: { xs: 2, md: 2.5 },
        background: `linear-gradient(160deg, ${alpha("#ffffff", 0.76)} 0%, ${alpha(appAppearance.colors.secondary, 0.12)} 100%)`,
      }}
    >
      <Stack spacing={2} height="100%">
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(appAppearance.colors.primary, 0.12),
                color: appAppearance.colors.primary,
                border: `1px solid ${appAppearance.colors.border}`,
              }}
            >
              <BusinessRounded fontSize="small" />
            </Box>
            <Typography
              variant="overline"
              sx={{
                color: appAppearance.colors.textMuted,
                letterSpacing: "0.14em",
                fontWeight: 700,
              }}
            >
              Client
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: appAppearance.colors.textSecondary,
              lineHeight: 1.7,
            }}
          >
            Review account details and recent reporting activity for this
            client.
          </Typography>
        </Stack>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: { xs: 140, md: 172 },
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            border: `1px solid ${appAppearance.colors.border}`,
            background: `linear-gradient(180deg, ${alpha(appAppearance.colors.primary, 0.12)} 0%, ${alpha("#ffffff", 0.74)} 100%)`,
          }}
        >
          {appVars ? (
            <Box
              component="img"
              src={`${appVars.api_url}/imageFiles/${clientID}.png`}
              alt="Client Logo"
              sx={{
                maxWidth: "82%",
                maxHeight: { xs: 92, md: 120 },
                objectFit: "contain",
                filter: `drop-shadow(0 12px 24px ${alpha(appAppearance.colors.primary, 0.18)})`,
              }}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <CircularProgress size={28} />
          )}
        </Box>

        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Chip label={`Client ${clientID}`} sx={softChipSx} />
          {appVars?.["application-version"] ? (
            <Chip
              label={`Version ${appVars["application-version"]}`}
              sx={softChipSx}
            />
          ) : null}
        </Stack>
      </Stack>
    </GlassPanel>
  );

  return (
    <GlassPageShell
      eyebrow="Platform Overview"
      title={appVars?.application || "Loading"}
      description={
        appLoading
          ? "Loading account details and recent activity."
          : "Review recent ecommerce activity, system health, and firing behavior across connected properties."
      }
      badge={
        appVars?.["application-version"]
          ? `Version ${appVars["application-version"]}`
          : "Live"
      }
      heroAside={heroAside}
    >
      <GlassPanel
        sx={{
          p: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${alpha("#ffffff", 0.76)} 0%, ${alpha(appAppearance.colors.accent, 0.08)} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(appAppearance.colors.secondary, 0.16),
                color: appAppearance.colors.primary,
                border: `1px solid ${appAppearance.colors.border}`,
              }}
            >
              <AutoAwesomeRounded fontSize="small" />
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: appAppearance.colors.textMuted,
                  letterSpacing: "0.16em",
                  fontWeight: 700,
                }}
              >
                Overview
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: appAppearance.colors.textPrimary,
                  fontWeight: 600,
                }}
              >
                Review recent activity and confirm your connected properties
                are reporting as expected.
              </Typography>
            </Box>
          </Stack>
          {appLoading ? (
            <Stack direction="row" spacing={1.25} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: appAppearance.colors.textSecondary }}>
                Loading information...
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </GlassPanel>

      <EcommerceStatus clientID={clientID} />
    </GlassPageShell>
  );
};

export default Hompepage;
